import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import urls from "../../../utils/url";
import { useParams, useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import Loader from "../../../components/common/loader";
import "../../../pages/central_monitor/central_monitor_graph/graph.scss";
import NavamGraph from "./NavamGraph";
import BackButton from "../../../components/common/BackButton";
import Cookie from "js-cookie";
import "./NavamChartLayout.scss";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

const NavamChartLayout = () => {
  const cookie = Cookie.get("access_token");
  const params = useParams();
  const navigate = useNavigate();
  const [keyarData, setKeyarData] = useState({});
  const [loading, setLoading] = useState(false);
  const [patientDetail, setPatientDetail] = useState({});
  let [startTime, setStartTime] = useState("");
  const [keyar, setKeyar] = useState({});
  const [isDelayedByOneMin, setIsDelayedByOneMin] = useState(false);
  const [accountType, setAccountType] = useState("");
  const [theme, setTheme] = useState();
  const [monitoringTime, setMonitoringTime] = useState("--:--:--");

  // Add accumulated datasets for graphs
  const [accumulatedHRData, setAccumulatedHRData] = useState({});
  const [accumulatedRRData, setAccumulatedRRData] = useState({});
  const animationIntervalRef = useRef(null);
  const sessionAnchorRef = useRef(null);
  const latestTimestampRef = useRef(null);

  const cookieUserData = Cookie.get("data");
  const cookieData = Cookie.get("theme");
  const [liveDelay, setLiveDelay] = useState("--");
  const playbackIndexRef = useRef(0);
  const playbackDataRef = useRef({
    spo2: [],
    pulse: [],
    hr: [],
    rr: [],
    temp: [],
  });

  const getPatientIdentifier = () => {
    if (
      patientDetail?.mrn &&
      patientDetail.mrn !== "NA" &&
      patientDetail.mrn !== "-" &&
      patientDetail.mrn.trim() !== ""
    ) {
      return { label: "MRN", value: patientDetail.mrn };
    }

    return { label: "ID", value: patientDetail?.patient_id };
  };

  const { value } = getPatientIdentifier();

  useEffect(() => {
    if (cookieUserData) {
      const parsedData = JSON.parse(cookieUserData);
      const account_type = parsedData.account_type;
      setAccountType(account_type);
    }
  }, [cookieUserData]);

  useEffect(() => {
    if (cookieData) {
      setTheme(cookieData);
    }
  }, [cookieData]);

  const RETENTION_WINDOW = 1800; // 30 minutes in seconds

  const pruneOldData = (dataset, currentTimestamp) => {
    if (!dataset || Object.keys(dataset).length === 0) return dataset;

    const cutoffTime = currentTimestamp - RETENTION_WINDOW;
    const pruned = {};

    Object.entries(dataset).forEach(([ts, value]) => {
      const normalizedTs =
        String(ts).length === 13 ? Number(ts) / 1000 : Number(ts);
      if (normalizedTs >= cutoffTime) {
        pruned[ts] = value;
      }
    });

    return pruned;
  };

  let spo2Data = {};
  let pulseData = {};
  let hrData = [];
  let rrData = [];
  let staticMonitoringTime = "--:--:--";

  // Process HR and RR data for graphs
  if (keyar.hrDataset && Object.keys(keyar.hrDataset).length > 0) {
    const sortedHRData = Object.entries(keyar.hrDataset)
      .filter(
        ([ts, value]) => value !== null && value !== undefined && value !== "",
      )
      .sort(([a], [b]) => parseInt(a) - parseInt(b));

    // Convert to Highcharts format: [timestamp_in_milliseconds, value]
    hrData = sortedHRData.map(([timestamp, value]) => [
      String(timestamp).length === 13
        ? Number(timestamp)
        : Number(timestamp) * 1000,
      parseFloat(value), // Keep 0 values as 0, don't convert to null
    ]);
  }

  if (keyar.rrDataset && Object.keys(keyar.rrDataset).length > 0) {
    const sortedRRData = Object.entries(keyar.rrDataset)
      .filter(
        ([ts, value]) => value !== null && value !== undefined && value !== "",
      )
      .sort(([a], [b]) => parseInt(a) - parseInt(b));

    // Convert to Highcharts format: [timestamp_in_milliseconds, value]
    rrData = sortedRRData.map(([timestamp, value]) => [
      String(timestamp).length === 13
        ? Number(timestamp)
        : Number(timestamp) * 1000,
      parseFloat(value), // Keep 0 values as 0, don't convert to null
    ]);
  }

  useEffect(() => {
    setLoading(true);
    fetchKeyarData(); // Only fetch detailed data for graphs
    // Start live data fetching interval (like chartLayout2)
    const intervalId = setInterval(() => {
      fetchLiveKeyarData();
    }, 10000); // Every 10 seconds

    // Initial live data fetch
    fetchLiveKeyarData();

    return () => clearInterval(intervalId);
  }, []);

  const isJson = (data) => {
    try {
      const jsonString = data.replace(/NaN/g, '"NaN"');
      return JSON.parse(jsonString);
    } catch (e) {
      return data;
    }
  };

  function formatTime(seconds) {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    const paddedMinutes = String(minutes).padStart(2, "0");
    const paddedSeconds = String(remainingSeconds).padStart(2, "0");

    return `${paddedMinutes} min ${paddedSeconds} sec`;
  }

  const handleClick = () => {
    navigate("/dashboard/central-monitor");
  };

  const fetchLiveKeyarData = async () => {
    try {
      const childPlacesCookie = Cookie.get("child_places");
      let placeIdsToUse = [];

      if (childPlacesCookie) {
        const places = JSON.parse(childPlacesCookie);
        placeIdsToUse = places.map((p) => Number(p.id));
      }

      if (placeIdsToUse.length === 0) {
        console.warn("No child places found → skipping live data API");
        return;
      }

      const placeQuery = placeIdsToUse.join(",");
      
      const res = await axios.get(
        `${urls.BETA_URL}v1/tesla/live-tests-summary/?place_ids=${placeQuery}`,
        {
          headers: {
            "Request-ID": "allow|me",
            "Content-Type": "application/json",
            Authorization: `Bearer ${cookie}`,
          },
        },
      );

      const jsonData = isJson(res.data);
      const data = jsonData.response;

      if (data.live_tests) {
        // Find the current patient's data
        const patientLiveData = Object.values(data.live_tests).find(
          (patient) => patient.patient_id == params.patientID,
        );

        if (
          patientLiveData &&
          patientLiveData.keyar_data_sets &&
          patientLiveData.keyar_data_sets[0]
        ) {
          const activeDataset = patientLiveData.keyar_data_sets[0];

          if (!sessionAnchorRef.current) {
            sessionAnchorRef.current = activeDataset.data_time;
            setStartTime(activeDataset.data_time);
          }

          const keyarParams = activeDataset.keyar_params || [];

          // Process the live data
          processLiveData(keyarParams);
        } else {
          // Patient not found - monitoring ended, redirect to central monitor
          sessionAnchorRef.current = null;
          setStartTime("");
          navigate("/dashboard/central-monitor");
        }
      } else {
        // No live tests data - redirect to central monitor
        sessionAnchorRef.current = null;
        setStartTime("");
        navigate("/dashboard/central-monitor");
      }
    } catch (error) {
      console.error("Failed to fetch live keyar data:", error);
    }
  };

  const processLiveData = (liveParams) => {
    const getSortedData = (paramType) => {
      const paramVariations = [paramType];

      if (paramType === "hr") paramVariations.push("mhr");
      if (paramType === "pulse") paramVariations.push("pulse_rate");

      let param = null;

      for (const variation of paramVariations) {
        param = liveParams.find((p) => p.param_type === variation);
        if (param) break;
      }

      if (!param?.latest_data) return [];

      return Object.entries(param.latest_data)
        .filter(
          ([ts, value]) =>
            value !== "0.0" &&
            value !== 0 &&
            value !== null &&
            value !== undefined,
        )
        .sort(([a], [b]) => parseInt(a) - parseInt(b));
    };

    let delay = "--";
    let delayInSecs = 0;

    const spo2Param = liveParams.find((p) => p.param_type === "spo2");
    const pulseParam = liveParams.find((p) => p.param_type === "pulse");

    const paramWithData =
      spo2Param?.latest_data && Object.keys(spo2Param.latest_data).length > 0
        ? spo2Param
        : pulseParam?.latest_data &&
            Object.keys(pulseParam.latest_data).length > 0
          ? pulseParam
          : null;

    if (paramWithData && paramWithData.latest_data) {
      const timestamps = Object.keys(paramWithData.latest_data);

      if (timestamps.length > 0) {
        // Find the latest timestamp (handle both 10 and 13 digit timestamps)
        const latestTimestamp = Math.max(
          ...timestamps.map((ts) => {
            const num = Number(ts);
            return String(ts).length === 13 ? num : num * 1000;
          }),
        );

        latestTimestampRef.current = latestTimestamp;

        const temp_delay = Date.now() - latestTimestamp;
        delayInSecs = Math.floor(temp_delay / 1000);
        delay = formatTime(delayInSecs);
      }
    } else {
      // Fallback: try to use any parameter with data for delay calculation
      for (const param of liveParams) {
        if (param.latest_data && Object.keys(param.latest_data).length > 0) {
          const timestamps = Object.keys(param.latest_data);
          if (timestamps.length > 0) {
            const latestTimestamp = Math.max(
              ...timestamps.map((ts) => {
                const num = Number(ts);
                return String(ts).length === 13 ? num : num * 1000;
              }),
            );

            const temp_delay = Date.now() - latestTimestamp;
            delayInSecs = Math.floor(temp_delay / 1000);
            delay = formatTime(delayInSecs);
            break;
          }
        }
      }
    }

    setIsDelayedByOneMin(delayInSecs > 60);

    // Get the full datasets for graphs (not just latest values)
    const hrParam = liveParams.find((p) => p.param_type === "hr");
    const rrParam = liveParams.find((p) => p.param_type === "rr");
    playbackDataRef.current = {
      spo2: getSortedData("spo2"),
      pulse: getSortedData("pulse"),
      hr: getSortedData("hr"),
      rr: getSortedData("rr"),
      temp: getSortedData("temp"),
    };
    playbackIndexRef.current = 0;

    // Accumulate HR data
    if (hrParam?.latest_data) {
      setAccumulatedHRData((prev) => {
        const merged = { ...prev, ...hrParam.latest_data };
        const currentTimestamp = Date.now() / 1000;
        return pruneOldData(merged, currentTimestamp);
      });
    }

    // Accumulate RR data
    if (rrParam?.latest_data) {
      setAccumulatedRRData((prev) => {
        const merged = { ...prev, ...rrParam.latest_data };
        const currentTimestamp = Date.now() / 1000;
        return pruneOldData(merged, currentTimestamp);
      });
    }

    // Update keyar state with all parameters
    setKeyar((prev) => {
      const currentTimestamp = Date.now() / 1000;
      const prunedHRDataset = prev.hrDataset
        ? pruneOldData(
            { ...prev.hrDataset, ...hrParam?.latest_data },
            currentTimestamp,
          )
        : hrParam?.latest_data || {};
      const prunedRRDataset = prev.rrDataset
        ? pruneOldData(
            { ...prev.rrDataset, ...rrParam?.latest_data },
            currentTimestamp,
          )
        : rrParam?.latest_data || {};

      return {
        spo2: prev.spo2 || "--",
        pulse: prev.pulse || "--",
        hr: prev.hr || "--",
        hrv: prev.hrv || "--",
        rr: prev.rr || "--",
        temp: prev.temp || "--",
        spo2Dataset: spo2Param?.latest_data || {},
        pulseDataset: pulseParam?.latest_data || {},
        // Use accumulated and pruned datasets for graphs
        hrDataset: prunedHRDataset,
        rrDataset: prunedRRDataset,
      };
    });
  };

  const fetchKeyarData = async () => {
    await axios
      .get(
        `${urls.BETA_URL}v1/nurse/patients/?patient_ids=${params.patientID}&forms=keyar_data,basic_detail,pa_pv_examination`,
        {
          headers: {
            "Request-ID": "allow|me",
            "Content-Type": "application/json",
            Authorization: `Bearer ${cookie}`,
          },
        },
      )
      .then((res) => {
        if (res.data) {
          const jsonData = isJson(res.data);
          const latest_keyar_data = jsonData.response[0].keyar_data;
          const patient_detail = jsonData.response[0].basic_detail;
          setPatientDetail({
            name: `${patient_detail.name}`,
            patient_id: `${patient_detail.id}`,
            mrn: `${patient_detail.mrn}`,
            dob: `${patient_detail.dob}`,
          });
          const keyar_data = latest_keyar_data[latest_keyar_data.length - 1];
          setKeyarData(keyar_data);
          setLoading(false);
        }
      })
      .catch((err) => {
        console.log("Graph API failed", err);
        setLoading(false);
      });
  };

  const sortTimestamp = (data) => {
    return Object.fromEntries(Object.entries(data).sort((a, b) => a[0] - b[0]));
  };

  if (keyarData != undefined || keyarData != null) {
    if (keyarData.params != undefined || keyarData.params != null) {
      let spo2_data =
        keyarData.params.filter((val) => val.param_type === "spo2")[0]
          ?.data_set || {};
      let pulse_data =
        keyarData.params.filter((val) => val.param_type === "pulse")[0]
          ?.data_set || {};

      if (
        !Object.keys(spo2Data || []).includes(
          Object.keys(keyar.spo2Dataset || []),
        )
      ) {
        Object.assign(spo2_data, keyar.spo2Dataset);
      }

      if (
        !Object.keys(pulseData || []).includes(
          Object.keys(keyar.pulseDataset || []),
        )
      ) {
        Object.assign(pulse_data, keyar.pulseDataset);
      }

      const sortedSpo2Data = sortTimestamp(spo2_data);
      const sortedPulseData = sortTimestamp(pulse_data);

      spo2Data = Object.entries(sortedSpo2Data).map(([key, value]) => [
        String(key).length == "13" ? Number(key / 1000) : Number(key),
        value == 0 ? null : Number(value),
      ]);
      pulseData = Object.entries(sortedPulseData).map(([key, value]) => [
        String(key).length == "13" ? Number(key / 1000) : Number(key),
        value == 0 ? null : Number(value),
      ]);

      // Calculate monitoring time from available data (prefer pulse, fallback to spo2, then hr, then rr)
      let monitoringDataSource = null;
      let monitoringDataName = "";
      if (pulseData && pulseData.length > 0) {
        monitoringDataSource = pulseData;
        monitoringDataName = "pulse";
      } else if (spo2Data && spo2Data.length > 0) {
        monitoringDataSource = spo2Data;
        monitoringDataName = "spo2";
      } else if (hrData && hrData.length > 0) {
        // Convert HR data back to the format needed for monitoring time calculation
        monitoringDataSource = hrData.map(([timestamp, value]) => [
          timestamp / 1000, // Convert back to seconds
          value,
        ]);
        monitoringDataName = "hr";
      } else if (rrData && rrData.length > 0) {
        // Convert RR data back to the format needed for monitoring time calculation
        monitoringDataSource = rrData.map(([timestamp, value]) => [
          timestamp / 1000, // Convert back to seconds
          value,
        ]);
        monitoringDataName = "rr";
      }

      if (monitoringDataSource && startTime) {
        let start_time = "";

        if (String(startTime).length == 13) {
          start_time = startTime;
        } else if (String(startTime).length == 10) {
          start_time = startTime * 1000;
        }

        const last_time =
          monitoringDataSource[monitoringDataSource.length - 1][0] * 1000;
        const timeDifferenceMs = last_time - start_time;

        // Convert milliseconds to hours, minutes, and seconds
        const hours = Math.floor(timeDifferenceMs / (1000 * 60 * 60));
        const minutes = Math.floor(
          (timeDifferenceMs % (1000 * 60 * 60)) / (1000 * 60),
        );
        const seconds = Math.floor((timeDifferenceMs % (1000 * 60)) / 1000);

        staticMonitoringTime = `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;

      }
    }
  }

  useEffect(() => {
    animationIntervalRef.current = setInterval(() => {
      const data = playbackDataRef.current;

      if (!data.spo2.length && !data.pulse.length && !data.hr.length) return;

      const maxLength = Math.max(
        data.spo2.length,
        data.pulse.length,
        data.hr.length,
        data.rr.length,
      );

      const index = playbackIndexRef.current % maxLength;

      const spo2Slice = data.spo2[index];
      const pulseSlice = data.pulse[index];
      const hrSlice = data.hr[index];
      const rrSlice = data.rr[index];
      const tempSlice = data.temp[index];

      const currentTs =
        spo2Slice?.[0] || pulseSlice?.[0] || hrSlice?.[0] || rrSlice?.[0];

      if (!currentTs) return;

      const normalizedTs =
        String(currentTs).length === 13
          ? Number(currentTs)
          : Number(currentTs) * 1000;

      const delaySecs = Math.floor((Date.now() - normalizedTs) / 1000);

      setLiveDelay(formatTime(delaySecs));
      setIsDelayedByOneMin(delaySecs > 60);

      if (startTime) {
        const startMs =
          String(startTime).length === 13
            ? Number(startTime)
            : Number(startTime) * 1000;

        const monitoringDiff = normalizedTs - startMs;

        const hours = Math.floor(monitoringDiff / (1000 * 60 * 60));
        const minutes = Math.floor(
          (monitoringDiff % (1000 * 60 * 60)) / (1000 * 60),
        );
        const seconds = Math.floor((monitoringDiff % (1000 * 60)) / 1000);

        setMonitoringTime(
          `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`,
        );
      }

      setKeyar((prev) => ({
        ...prev,
        spo2: spo2Slice ? parseFloat(spo2Slice[1]) : prev.spo2,
        pulse: pulseSlice ? parseFloat(pulseSlice[1]) : prev.pulse,
        hr: hrSlice ? parseFloat(hrSlice[1]) : prev.hr,
        rr: rrSlice ? parseFloat(rrSlice[1]) : prev.rr,
        temp: tempSlice ? parseFloat(tempSlice[1]) : prev.temp,
      }));

      playbackIndexRef.current += 1;
    }, 1000);

    return () => clearInterval(animationIntervalRef.current);
  }, [startTime]);

  return (
    <>
      <div
        className={`${theme === "light-theme" ? "chart-light" : "chart-dark"}`}
      >
        {loading ? (
          <Loader />
        ) : (
          <div
            style={{
              background: theme === "light-theme" ? `#fff` : `#0E0E0E`,
              height: `100vh`,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              className={`navbar ${theme === "light-theme" ? "navbar-white" : "navbar-dark"}`}
            >
              <div className="nav">
                <div
                  onClick={handleClick}
                  className={
                    theme === "light-theme" ? "back-light" : "back-dark"
                  }
                >
                  <BackButton theme={theme} />
                </div>
                <p className="live-view">Live View</p>
                <p className="delayed-timer">Delayed By: - {liveDelay}</p>
              </div>
            </div>
            {isDelayedByOneMin === true ? (
              <div className="delayedByOneMin-div-navam">
                <img src="/warning.png" alt="" className="small-warning" />
                <p className="delayedByOneMin-text">
                  Data loading is delayed by more than 1 min
                </p>
              </div>
            ) : (
              <div />
            )}

            <div className="patient-detail-navam">
              <div className="graph-layout-navam">
                <div
                  className={`detail-navam ${theme === "light-theme" ? "light-theme" : "dark-theme"}`}
                >
                  <p className="name">
                    {patientDetail?.name} ({value})
                  </p>
                  <p className="age">
                    DOB :{" "}
                    {patientDetail?.dob == "undefined" ||
                    patientDetail?.dob == null ||
                    patientDetail?.dob == ""
                      ? "--"
                      : patientDetail?.dob}
                  </p>
                  {/* <p className="device-id">Device ID:{deviceID}</p> */}
                  <p className="monitoring-time">
                    Monitoring Time : {monitoringTime}
                  </p>
                </div>

                <NavamGraph
                  accountType={accountType}
                  spo2Data={spo2Data || []}
                  pulseData={pulseData || []}
                  hrData={hrData || []}
                  rrData={rrData || []}
                  startTime={startTime}
                />
              </div>
              <div
                className={` detail-layout-navam ${theme === "light-theme" ? "detail-layout-light" : "detail-layout-dark"}`}
              >
                <div className="fhr-fhr2-mhr-card-navam">
                  <p className="monitoring-data">Monitoring Data</p>
                  <hr className="hr" />
                  <div className="monitoring-data-layout">
                    <div>
                      <div className="fhr-layout-card">
                        <div className="name-layout">
                          <p className="fhr">
                            SpO<sub>2</sub>
                          </p>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-evenly",
                            }}
                          >
                            {/* <img src={theme === "light-theme" ? '/fhr-light.svg' : '/fhr-yellow.png'} alt="FHR Logo" className='imageLogo'/>
                            <img src={theme === "light-theme" ? '/fhr-light1.svg' : '/fhr-yellow1.png'} alt="FHR Logo" className='imageLogo'/> */}
                          </div>
                        </div>

                        <div className="number-layout">
                          <p className={"number"}>
                            {!keyar?.spo2 ||
                            keyar?.spo2 == "" ||
                            keyar?.spo2 == 0
                              ? "--"
                              : keyar?.spo2}
                          </p>
                          <p className="bpm">%</p>
                        </div>
                      </div>

                      <div className="fhr-layout-card">
                        <div className="name-layout">
                          <p className="fhr">Pulse</p>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-evenly",
                            }}
                          >
                            {/* <img src={theme === "light-theme" ? '/fhr2-light.svg' : '/fhr2-blue.png'} alt="FHR2 Logo" className='imageLogo'/>
                            <img src={theme === "light-theme" ? '/fhr2-light2.svg' : '/fhr2-blue1.png'} alt="FHR2 Logo" className='imageLogo'/> */}
                          </div>
                        </div>

                        <div className="number-layout">
                          <p className={"number"}>
                            {!keyar?.pulse ||
                            keyar?.pulse == "" ||
                            keyar?.pulse == 0
                              ? "--"
                              : keyar?.pulse}
                          </p>
                          <p className="bpm">BPM</p>
                        </div>
                      </div>

                      <div className="fhr-layout-card">
                        <div className="name-layout">
                          <p className="fhr">HR</p>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-evenly",
                            }}
                          >
                            {/* <img src={theme === "light-theme" ? '/mhr-light.svg' : '/mhr-pink.png'} alt="MHR Logo" className='imageLogo'  width={25} height={25}/>
                            <img src={theme === "light-theme" ? '/mhr-light2.svg' : '/mhr-pink.png'} alt="MHR Logo" className='imageLogo'/> */}
                          </div>
                        </div>

                        <div className="number-layout">
                          <p className={"number"}>
                            {keyar?.hr && keyar.hr > 0
                              ? Math.floor(keyar.hr)
                              : "--"}
                          </p>
                          <p className="bpm">BPM</p>
                        </div>
                      </div>

                      <div className="fhr-layout-card">
                        <div className="name-layout">
                          <p className="fhr">RR</p>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-evenly",
                            }}
                          >
                            {/* <img src={'/spo2.svg'} alt="MHR Logo" className='imageLogo'  width={25} height={25}/> */}
                          </div>
                        </div>

                        <div className="number-layout">
                          <p className="number">
                            {keyar?.rr && keyar.rr > 0
                              ? Math.floor(keyar.rr)
                              : "--"}
                          </p>
                          <p className="bpm">BPM</p>
                        </div>
                      </div>

                      <div className="fhr-layout-card">
                        <div className="name-layout">
                          <p className="fhr">HRV</p>
                        </div>
                        <div className="number-layout">
                          <p className="number">
                            {keyar?.hrv && keyar.hrv > 0
                              ? Math.floor(keyar.hrv)
                              : "--"}
                          </p>
                          <p className="bpm">ms</p>
                        </div>
                      </div>

                      <div className="fhr-layout-card">
                        <div className="name-layout">
                          <p className="fhr">Temperature</p>
                        </div>
                        <div className="number-layout">
                          <p className="number">
                            {keyar?.temp && keyar.temp > 0
                              ? keyar.temp.toFixed(1)
                              : "--"}
                          </p>
                          <p className="bpm">°C</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

const mapStateToProps = () => ({
  // No longer need keyar_data from Redux since we fetch directly
});

export default connect(mapStateToProps)(NavamChartLayout);
